import { useMemo } from "react"

import { useBounceGlobalValues, useTranslation } from "@bounce/i18n"

import type { FAQ } from "../../../utils/boilerplate"

export const useFAQs = () => {
  const { t } = useTranslation("marketing-partner")
  const bounceGlobals = useBounceGlobalValues()

  const insuranceAmount = bounceGlobals.bounceShieldProtection
  const faqs: FAQ[] = useMemo(
    () => [
      {
        id: "I don’t have any space to store luggage or packages. Can I still be a Bounce partner?",
        question: t(
          "cmp.partnerFaq.iDontHaveS.question",
          "I don’t have any space to store luggage or packages. Can I still be a Bounce partner?",
        ),
        answer: t(
          "cmp.partnerFaq.iDontHaveS.answer",
          "While we suggest space for at least 5 items, there is no minimum or maximum capacity requirements. The more space you have, the more earning potential you have. You determine what your capacity is. If you need someone from the Bounce team to help you figure that out, just let us know!",
        ),
      },
      {
        id: "What does Bounce do?",
        question: t("cmp.partnerFaq.whatIsBounce.question", "What does Bounce do?"),
        answer: t(
          "cmp.partnerFaq.whatIsBounce.answer",
          "Bounce provides a platform that allows businesses to capitalize on their unused space. We connect them with customers who want to securely store their things for a few hours or days in an easily accessible location.",
        ),
      },
      {
        id: "How would a Bounce partnership benefit me?",
        question: t("cmp.partnerFaq.whyBounce.question", "How would a Bounce partnership benefit me?"),
        answer: t(
          "cmp.partnerFaq.whyBounce.answer",
          "A partnership with Bounce means you'll have an additional revenue stream, and new way to bring more people through your doors. We compensate you for any items you store through our service. You have the ability to earn even more by referring customers. Most importantly, we’ll drive a steady stream of new traffic to your store, providing associated revenue and brand awareness for your business.",
        ),
      },
      {
        id: "How will I know a customer is coming and how many items they have?",
        question: t(
          "cmp.partnerFaq.whenWillTheyArrive.question",
          "How will I know a customer is coming and how many items they have?",
        ),
        answer: t(
          "cmp.partnerFaq.whenWillTheyArrive.answerV2",
          "We provide you with notifications of new bookings, including how many items need to be stored and the customer’s anticipated drop-off and pickup times. Our partners have full control of their available space and hours. Our platform makes customer handling easy for you and your team.",
        ),
      },
      {
        id: "What is your commitment to Bounce partners?",
        question: t(
          "cmp.partnerFaq.bounceCommitmentToPartners.question",
          "What is your commitment to Bounce partners?",
        ),
        answer: t(
          "cmp.partnerFaq.bounceCommitmentToPartners.answer",
          "We want to make sure the Bounce experience is fantastic for both our customers and our partners. We commit to creating an easy, hassle-free experience for our partners and their teams.",
        ),
      },
      {
        id: "How do I sign up to be a Bounce partner?",
        question: t("cmp.partnerFaq.howDoISignup.question", "How do I sign up to be a Bounce partner?"),
        answer: t(
          "cmp.partnerFaq.howDoISignup.answer",
          "We make it easy to become a Bounce partner. Simply fill out your information on this page, or email partners@usebounce.com. We'll answer any questions you might have, and walk you through our simple onboarding process from there.",
        ),
      },
      {
        id: "What about security issues? Is there insurance for the bags?",
        question: t(
          "cmp.partnerFaq.whatAboutSecurity.question2",
          "What about security issues? Is there insurance for the bags?",
        ),
        answer: t(
          "cmp.partnerFaq.whatAboutSecurity.answerV3",
          "Our bag protection covers up to {{insuranceAmount}} for customers' items. We provide you with security tags for customers' bags, each with a unique identifying number. As all payments are done online, we track who stores items with you and how many items are stored.",
          { insuranceAmount },
        ),
      },
    ],
    [t, insuranceAmount],
  )

  return faqs
}
